import type {
  GroupParameterValue,
  InteractionState,
  RenderCommand,
  RouteModalMode,
  SerializableAssignee,
  SerializableBounds,
  SerializableConnection,
  SerializableOrder,
  SerializableRoute,
  SerializableState,
  SerializableWorkspace,
  WorkerCommand,
} from "../workers/dispatch/types";

import { renderLoop } from "./renderLoop";

class WorkerBridge {
  // Call on unmount
  destroy(): void {
    this.worker?.terminate();
    this.worker = null;
    renderLoop.stop();
  }

  // Call once on app mount (e.g. inside the dispatch page component)
  init(): void {
    if (this.worker) return;

    this.worker = new Worker(
      new URL("../workers/dispatch/index.ts", import.meta.url),
      { type: "module" },
    );

    this.worker.onmessage = (e: MessageEvent<RenderCommand>) => {
      renderLoop.enqueue(e.data);
    };

    this.worker.onerror = (err) => {
      console.error("[DispatchWorker]", err);
    };

    renderLoop.start();
  }

  // ---- Typed convenience methods ----

  initOrders(
    orders: SerializableOrder[],
    routes: SerializableRoute[],
    interaction: InteractionState,
  ): void {
    this.send({ interaction, orders, routes, type: "INIT" });
  }

  removeOrders(orderIds: string[]): void {
    this.send({ orderIds, type: "ORDERS_REMOVE" });
  }

  removeRoutes(routeIds: string[]): void {
    this.send({ routeIds, type: "ROUTES_REMOVE" });
  }

  private send(cmd: WorkerCommand): void {
    if (!this.worker) {
      console.warn("[WorkerBridge] send called before init()");
      return;
    }
    this.worker.postMessage(cmd);
  }

  setAssignees(assignees: SerializableAssignee[]): void {
    this.send({ assignees, type: "SET_ASSIGNEES" });
  }

  setBounds(bounds: SerializableBounds): void {
    this.send({ bounds, type: "SET_BOUNDS" });
  }

  setEditRoute(orderIds: string[], routeModalMode: RouteModalMode): void {
    this.send({ orderIds, routeModalMode, type: "SET_EDIT_ROUTE" });
  }

  setFocus(orderId: string | null): void {
    this.send({ orderId, type: "SET_FOCUS" });
  }

  setGroup(groupParameter: GroupParameterValue): void {
    this.send({ groupParameter, type: "SET_GROUP" });
  }

  setHover(orderId: string | null): void {
    this.send({ orderId, type: "SET_HOVER" });
  }

  setRouteFocus(routeId: string | null): void {
    this.send({ routeId, type: "SET_ROUTE_FOCUS" });
  }

  setStates(states: SerializableState[]): void {
    this.send({ states, type: "SET_STATES" });
  }

  setWorkspaceData(
    workspaces: SerializableWorkspace[],
    connections: SerializableConnection[],
    currentWorkspaceId: string,
  ): void {
    this.send({
      connections,
      currentWorkspaceId,
      type: "SET_WORKSPACE_DATA",
      workspaces,
    });
  }

  setZoom(zoom: number): void {
    this.send({ type: "SET_ZOOM", zoom });
  }

  upsertOrders(orders: SerializableOrder[]): void {
    this.send({ orders, type: "ORDERS_UPSERT" });
  }

  upsertRoutes(routes: SerializableRoute[]): void {
    this.send({ routes, type: "ROUTES_UPSERT" });
  }

  private worker: Worker | null = null;
}

// Singleton — import this wherever you need to talk to the worker
export const workerBridge = new WorkerBridge();
